/**
 * Admin 出局上限表 — one row per (path × package × coin).
 * Overlay: uo_config `reward.static.exit_cap_rows`.
 */
import { Decimal } from 'decimal.js';
import {
  E1_REGISTER_EXIT_MULTIPLIER,
  E1_TO_E2_UPGRADE_EXIT_MULTIPLIER,
  LEGACY_REGISTER_EXIT_MULTIPLIER,
  MAIN_EXIT_MULTIPLIER,
  MAIN_STAR_PRINCIPAL_USD,
  NEW_SYSTEM_REGISTER_EXIT_MULTIPLIER,
  type AccountOrigin,
  type StarPackage,
  type StaticExitRule,
} from './static-reward.js';

export type ExitCapRowKind = 'register_new' | 'register_legacy' | 'main' | 'upgrade';
export type ExitSnapshotKind = 'upgrade';

export interface ExitCapTableRowDef {
  id: string;
  kind: ExitCapRowKind;
  path_zh: string;
  star_package: StarPackage;
  coin_label: string;
  pay_coins: string[];
  principal_usd: string;
  exit_multiplier: string;
}

export interface ExitCapOverlayEntry {
  principal_usd?: string;
  exit_multiplier?: string;
}

export type ExitCapOverlay = Record<string, ExitCapOverlayEntry>;

export const EXIT_CAP_CONFIG_KEY = 'reward.static.exit_cap_rows';

export const EXIT_CAP_TABLE_ROWS: ExitCapTableRowDef[] = [
  {
    id: 'new_e1_sp1',
    kind: 'register_new',
    path_zh: '新系统注册 / 开子',
    star_package: 'E1',
    coin_label: 'SP1',
    pay_coins: ['sp1'],
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E1,
    exit_multiplier: E1_REGISTER_EXIT_MULTIPLIER,
  },
  {
    id: 'new_e1_rp',
    kind: 'register_new',
    path_zh: '新系统注册 / 开子',
    star_package: 'E1',
    coin_label: 'RP',
    pay_coins: ['rp'],
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E1,
    exit_multiplier: E1_REGISTER_EXIT_MULTIPLIER,
  },
  {
    id: 'new_e1_sp3',
    kind: 'register_new',
    path_zh: '新系统注册 / 开子',
    star_package: 'E1',
    coin_label: 'SP3',
    pay_coins: ['sp3'],
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E1,
    exit_multiplier: E1_REGISTER_EXIT_MULTIPLIER,
  },
  {
    id: 'new_e2_sp1',
    kind: 'register_new',
    path_zh: '新系统注册 / 开子',
    star_package: 'E2',
    coin_label: 'SP1',
    pay_coins: ['sp1'],
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E2,
    exit_multiplier: NEW_SYSTEM_REGISTER_EXIT_MULTIPLIER.sp1,
  },
  {
    id: 'new_e2_rp',
    kind: 'register_new',
    path_zh: '新系统注册 / 开子',
    star_package: 'E2',
    coin_label: 'RP',
    pay_coins: ['rp'],
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E2,
    exit_multiplier: NEW_SYSTEM_REGISTER_EXIT_MULTIPLIER.rp,
  },
  {
    id: 'new_e2_sp3',
    kind: 'register_new',
    path_zh: '新系统注册 / 开子',
    star_package: 'E2',
    coin_label: 'SP3',
    pay_coins: ['sp3'],
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E2,
    exit_multiplier: NEW_SYSTEM_REGISTER_EXIT_MULTIPLIER.sp3,
  },
  {
    id: 'new_e3_rp_sp3',
    kind: 'register_new',
    path_zh: '新系统注册 / 开子',
    star_package: 'E3',
    coin_label: 'RP / SP3',
    pay_coins: ['rp', 'sp3'],
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E3,
    exit_multiplier: NEW_SYSTEM_REGISTER_EXIT_MULTIPLIER.rp,
  },
  {
    id: 'legacy_e1_sp2',
    kind: 'register_legacy',
    path_zh: '旧系统跟入',
    star_package: 'E1',
    coin_label: 'SP2',
    pay_coins: ['sp2'],
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E1,
    exit_multiplier: LEGACY_REGISTER_EXIT_MULTIPLIER.sp2,
  },
  {
    id: 'legacy_e1_sp1_rp_sp3',
    kind: 'register_legacy',
    path_zh: '旧系统跟入',
    star_package: 'E1',
    coin_label: 'SP1 / RP / SP3',
    pay_coins: ['sp1', 'rp', 'sp3'],
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E1,
    exit_multiplier: E1_REGISTER_EXIT_MULTIPLIER,
  },
  {
    id: 'legacy_e2_sp2',
    kind: 'register_legacy',
    path_zh: '旧系统跟入',
    star_package: 'E2',
    coin_label: 'SP2',
    pay_coins: ['sp2'],
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E2,
    exit_multiplier: LEGACY_REGISTER_EXIT_MULTIPLIER.sp2,
  },
  {
    id: 'legacy_e2_sp1_rp_sp3',
    kind: 'register_legacy',
    path_zh: '旧系统跟入',
    star_package: 'E2',
    coin_label: 'SP1 / RP / SP3',
    pay_coins: ['sp1', 'rp', 'sp3'],
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E2,
    exit_multiplier: LEGACY_REGISTER_EXIT_MULTIPLIER.rp,
  },
  {
    id: 'legacy_e3_sp2',
    kind: 'register_legacy',
    path_zh: '旧系统跟入',
    star_package: 'E3',
    coin_label: 'SP2',
    pay_coins: ['sp2'],
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E3,
    exit_multiplier: LEGACY_REGISTER_EXIT_MULTIPLIER.sp2,
  },
  {
    id: 'upgrade_e1_e2',
    kind: 'upgrade',
    path_zh: '100 原点升 1000',
    star_package: 'E2',
    coin_label: '—',
    pay_coins: [],
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E2,
    exit_multiplier: E1_TO_E2_UPGRADE_EXIT_MULTIPLIER,
  },
  {
    id: 'main_e1',
    kind: 'main',
    path_zh: '主号开通（SP2+TP）',
    star_package: 'E1',
    coin_label: '—',
    pay_coins: [],
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E1,
    exit_multiplier: MAIN_EXIT_MULTIPLIER.E1,
  },
  {
    id: 'main_e2',
    kind: 'main',
    path_zh: '主号开通（SP2+TP）',
    star_package: 'E2',
    coin_label: '—',
    pay_coins: [],
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E2,
    exit_multiplier: MAIN_EXIT_MULTIPLIER.E2,
  },
  {
    id: 'main_e3',
    kind: 'main',
    path_zh: '主号开通（SP2+TP）',
    star_package: 'E3',
    coin_label: '—',
    pay_coins: [],
    principal_usd: MAIN_STAR_PRINCIPAL_USD.E3,
    exit_multiplier: MAIN_EXIT_MULTIPLIER.E3,
  },
];

export const EXIT_CAP_ROW_BY_ID: Record<string, ExitCapTableRowDef> = Object.fromEntries(
  EXIT_CAP_TABLE_ROWS.map((r) => [r.id, r]),
);

export function exitCapUsd(principalUsd: string, exitMultiplier: string): string {
  return new Decimal(principalUsd).mul(exitMultiplier).toString();
}

export function applyExitCapOverlay(
  def: ExitCapTableRowDef,
  overlay: ExitCapOverlay,
): StaticExitRule & { cap_usd: string; principal_source: 'uo_config' | 'code_default'; multiplier_source: 'uo_config' | 'code_default' } {
  const o = overlay[def.id];
  const principal_usd = o?.principal_usd ?? def.principal_usd;
  const exit_multiplier = o?.exit_multiplier ?? def.exit_multiplier;
  return {
    principal_usd,
    exit_multiplier,
    cap_usd: exitCapUsd(principal_usd, exit_multiplier),
    principal_source: o?.principal_usd ? 'uo_config' : 'code_default',
    multiplier_source: o?.exit_multiplier ? 'uo_config' : 'code_default',
  };
}

export function matchExitCapTableRow(opts: {
  snapshotKind?: ExitSnapshotKind;
  starPackage: StarPackage;
  origin?: AccountOrigin;
  payCoin?: string;
}): ExitCapTableRowDef | undefined {
  if (opts.snapshotKind === 'upgrade') {
    return EXIT_CAP_ROW_BY_ID.upgrade_e1_e2;
  }
  if (opts.payCoin) {
    const kind: ExitCapRowKind = opts.origin === 'legacy' ? 'register_legacy' : 'register_new';
    const coin = opts.payCoin.trim().toLowerCase();
    return EXIT_CAP_TABLE_ROWS.find(
      (r) => r.kind === kind && r.star_package === opts.starPackage && r.pay_coins.includes(coin),
    );
  }
  return EXIT_CAP_TABLE_ROWS.find((r) => r.kind === 'main' && r.star_package === opts.starPackage);
}
